import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { showToast } from '@/shared/utils/showToast';
import {
  cancelAdminOrder,
  confirmAdminOrderPayment,
  fetchAdminOrder,
  fetchAdminOrderLabel,
  fetchAdminOrderSummary,
  fetchAdminOrders,
  openAdminOrderEvents,
  rejectAdminOrderTransfer,
  requestAdminOrderPickup,
} from '../apis/order';
import type { AdminOrderEvent, AdminOrderParams } from '../types/order.types';

const ORDERS_KEY = ['admin-orders'];

/** Riwayat Transaksi total cards. */
export const useAdminOrderSummary = () =>
  useQuery({ queryKey: [...ORDERS_KEY, 'summary'], queryFn: fetchAdminOrderSummary });

/** Daftar Transaksi; the previous page stays on screen while the next one loads. */
export const useAdminOrders = (params: AdminOrderParams) =>
  useQuery({
    queryKey: [...ORDERS_KEY, 'list', params],
    queryFn: () => fetchAdminOrders(params),
    placeholderData: keepPreviousData,
  });

/** Detail Transaksi; nothing is requested without an id. */
export const useAdminOrder = (orderId: string | undefined) =>
  useQuery({
    queryKey: [...ORDERS_KEY, 'detail', orderId],
    queryFn: () => fetchAdminOrder(orderId as string),
    enabled: Boolean(orderId),
  });

type ActionToast = { type?: 'success' | 'warning'; title: string; message: string };

/** Every action changes the list, the cards, the detail and the dashboard. */
const useOrderAction = <TVariables, TResult = unknown>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
  success: ActionToast | ((result: TResult) => ActionToast)
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (result) => {
      const { type = 'success', ...toast } =
        typeof success === 'function' ? success(result) : success;
      showToast({ type, ...toast });
      queryClient.invalidateQueries({ queryKey: ORDERS_KEY });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error: Error) => {
      showToast({ type: 'error', title: 'Gagal', message: error.message });
      queryClient.invalidateQueries({ queryKey: ORDERS_KEY });
    },
  });
};

export const useCancelAdminOrder = () =>
  useOrderAction((orderId: string) => cancelAdminOrder(orderId), {
    title: 'Transaksi dibatalkan',
    message: 'Pesanan berhasil dibatalkan.',
  });

export const useRequestAdminOrderPickup = () =>
  useOrderAction(
    (orderId: string) => requestAdminOrderPickup(orderId),
    (result) => ({
      title: 'Penjemputan diminta',
      message: result.tracking_number
        ? `Kurir akan menjemput paket. Resi: ${result.tracking_number}.`
        : 'Permintaan penjemputan berhasil dikirim.',
    })
  );

export const useConfirmAdminOrderPayment = () =>
  useOrderAction(
    (orderId: string) => confirmAdminOrderPayment(orderId),
    (result) =>
      result.pickup_error
        ? {
            type: 'warning',
            title: 'Pembayaran dikonfirmasi',
            message: `Paket belum diserahkan ke kurir (${result.pickup_error.replace(/\.$/, '')}). Coba lagi lewat Request Jemput.`,
          }
        : {
            title: 'Pembayaran dikonfirmasi',
            message: result.tracking_number
              ? `Pesanan diproses. Kurir akan menjemput paket, resi: ${result.tracking_number}.`
              : 'Pesanan diproses.',
          }
  );

export const useRejectAdminOrderTransfer = () =>
  useOrderAction(rejectAdminOrderTransfer, {
    title: 'Transfer ditandai tidak ditemukan',
    message: 'Pembeli dapat melaporkan transfernya kembali.',
  });

// A blob URL is revoked after a while; the opened tab has read the PDF by then
const LABEL_URL_TTL_MS = 60_000;

/** Cetak Resi: opens the courier label (PDF) in a new tab, where the browser's viewer prints it. The tab
 *  is opened on the click itself, before the PDF loads, so the browser does not block it. */
export const usePrintAdminOrderLabel = () => {
  const mutation = useMutation({ mutationFn: fetchAdminOrderLabel });
  const print = (orderId: string) => {
    const tab = window.open('', '_blank');
    if (!tab) {
      showToast({
        type: 'error',
        title: 'Gagal',
        message: 'Browser memblokir tab baru. Izinkan pop-up untuk situs ini lalu coba lagi.',
      });
      return;
    }
    tab.opener = null;
    tab.document.title = 'Cetak Resi';
    tab.document.body.textContent = 'Memuat resi...';
    mutation.mutate(orderId, {
      onSuccess: (pdf) => {
        const url = URL.createObjectURL(pdf);
        tab.location.href = url;
        window.setTimeout(() => URL.revokeObjectURL(url), LABEL_URL_TTL_MS);
      },
      onError: (error: Error) => {
        tab.close();
        showToast({ type: 'error', title: 'Gagal', message: error.message });
      },
    });
  };
  return { print, isPending: mutation.isPending };
};

/** Live alerts while an admin page is open: a reported transfer reloads the orders and the
 *  dashboard, then calls onTransferReported. */
export const useAdminOrderEvents = (onTransferReported: (event: AdminOrderEvent) => void) => {
  const queryClient = useQueryClient();
  const handler = useRef(onTransferReported);
  handler.current = onTransferReported;

  useEffect(() => {
    if (typeof EventSource === 'undefined') return;
    const reload = () => {
      queryClient.invalidateQueries({ queryKey: ORDERS_KEY });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    };
    const source = openAdminOrderEvents();
    let connected = false;
    source.onopen = () => {
      // Events sent while the connection was down are lost: reload what they would have changed
      if (connected) reload();
      connected = true;
    };
    source.addEventListener('transfer_reported', (message) => {
      reload();
      handler.current(JSON.parse((message as MessageEvent<string>).data));
    });
    return () => source.close();
  }, [queryClient]);
};
